import React, {useEffect, useState} from 'react'
import PageWrapper from '../../components/shared/page-wrapper'
import CustomTable from '../../components/shared/table';
import {useAppDispatch, useAppSelector} from '../../hooks/hooks';
import {getAdminPatients, getPatients} from '../../features/patients';
import styles from './styles.module.scss'
import { SVGS } from '../../assets/images.js';
import { StateStatus } from '../../features/loader/types';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Close';
import { Box, IconButton, InputAdornment, TextField, ToggleButton, ToggleButtonGroup, useMediaQuery } from '@mui/material';
import { Patient } from '../../features/patients/types';
import moment from 'moment';

export default function PatientsPage() {
    const dispatch = useAppDispatch();
    const loadingStatus = useAppSelector(store => store.loader.status);
    const patients = useAppSelector(x => x.patients);
    const profile = useAppSelector(x => x.user.userProfile);
    const [data, setData] = useState<Array<Patient>>([]);
    const [searchValue, setSearchValue] = useState('');
    const [patientScope, setPatientScope] = useState<'mine' | 'others'>('mine');
    const isDesktop = useMediaQuery('(min-width:1000px)');
    const isAdmin = profile.role === 'admin';

    const find = (data: Patient[], searchString: string): Patient[] => {
        const searchArray = searchString.toLowerCase().split(' ');
        return data.filter(x => {
            const searchable = [x.userName, x.userEmail, x.name].filter(Boolean).join(' ').toLowerCase();
            return searchArray.every(el => {
                return searchable.includes(el);
            });
        });
    }

    useEffect(() => {
        if (profile.id === '') return;
        if (isAdmin) {
            dispatch(getAdminPatients());
        } else {
            dispatch(getPatients(0));
        }
    }, [profile.id, profile.role]);

    useEffect(() => {
        let filtered = patients.items ?? [];
        if (isAdmin) {
            filtered = filtered.filter(patient => patientScope === 'mine'
                ? patient.userId === profile.id
                : patient.userId !== profile.id);
        }
        if (searchValue !== '') {
            filtered = find(filtered, searchValue);
        }
        setData(filtered);
    }, [searchValue, patientScope, patients.items, profile.id, profile.role]);

    const onScroll = (e: any) => {
        // TODO: currently we're loading full data
        // const bottom = e.target.scrollHeight - e.target.scrollTop < e.target.clientHeight + 1;
        // if (bottom && !full) {
        //     handleChangePage(page + 1)
        // }
    }

    const sort = (a: Patient, b: Patient): number => {
        const at = a.sessions && a.sessions.length > 0 ? a.sessions[0].endDate : null;
        const bt = b.sessions && b.sessions.length > 0 ? b.sessions[0].endDate : null;
        if (at === null && bt === null) {
            return 0;
        } else if (at === null && bt !== null) {
            return 1;
        } else if (at !== null && bt === null) {
            return -1;
        }

        const atMoment = moment(at);

        if (atMoment.isAfter(bt)){
            return -1;
        } else{
            return 2
        }
    }

    const patientGroups = data.reduce<Array<{ userId: string; userName?: string; userEmail?: string; patients: Patient[] }>>((groups, patient) => {
        const userId = patient.userId ?? '';
        const group = groups.find(item => item.userId === userId);
        if (group) {
            group.patients.push(patient);
        } else {
            groups.push({ userId, userName: patient.userName, userEmail: patient.userEmail, patients: [patient] });
        }
        return groups;
    }, []);

    const userTitle = (group: typeof patientGroups[number]) => {
        if (group.userName && group.userEmail) return `${group.userName} (${group.userEmail})`;
        return group.userName || group.userEmail || 'Unknown user';
    };

    return <PageWrapper>
        <Box sx={{ width: '100%', height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Box className={styles.searchControls}>
                <TextField
                    placeholder={isAdmin ? 'Search by users or patients' : 'Search by patients'}
                    variant="outlined"
                    className={styles.search}
                    sx={{ width: isDesktop ? '390px' : '100%', margin: '0px !important' }}
                    value={searchValue}
                    onChange={(e) => {
                        if (loadingStatus !== StateStatus.PENDING) { setSearchValue(e.target.value) }
                    }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>,
                        endAdornment: <InputAdornment position='end'>
                            {
                                searchValue !== '' && <IconButton onClick={() => setSearchValue('')}>
                                    <ClearIcon />
                                </IconButton>
                            }
                        </InputAdornment>
                    }}
                />
                {isAdmin && <ToggleButtonGroup
                    value={patientScope}
                    exclusive
                    disabled={loadingStatus === StateStatus.PENDING}
                    onChange={(_, value: 'mine' | 'others' | null) => value && setPatientScope(value)}
                    aria-label="Patient ownership filter"
                >
                    <ToggleButton value="mine">Only mine</ToggleButton>
                    <ToggleButton value="others">Other users</ToggleButton>
                </ToggleButtonGroup>}
            </Box>
            {
                loadingStatus !== StateStatus.PENDING && <>
                    {
                        searchValue !== '' && data.length < 1 ? <div className={styles.notFoundWrapper}>
                            <div className={styles.notFoundIcon}>
                                <SVGS.CloseIcon />
                            </div>
                            <h3>No result</h3>
                            <p>{isAdmin
                                ? 'We couldn\'t find a user or patient with this name.'
                                : 'We couldn\'t find a patient with this name.'}</p>
                        </div> : <>
                            <div className={styles.patientsFound}>
                                {
                                    searchValue !== '' ? <div className={styles.patientSearch}>
                                        {
                                            isDesktop ? <h1>Found {data.length} of {patients.total}</h1>
                                                : <p>Found {data.length} of {patients.total}</p>
                                        }
                                    </div> : patients.total > 0 ? <h1 className={styles.patientsTotal}>{patients.total} patients</h1> : <></>
                                }
                            </div>
                            {
                                patients && patients.items && patients.total > 0 && data && data.length > 0 ? <div className={styles.patientsTableWrapper}>
                                    {isAdmin && patientScope === 'others' ? patientGroups.map(group => <section key={group.userId} className={styles.userGroup}>
                                        <h2>{userTitle(group)}</h2>
                                        <CustomTable
                                            data={group.patients}
                                            total={group.patients.length}
                                            onScroll={onScroll}
                                            sort={sort}
                                            isReadOnly={() => true}
                                        />
                                    </section>) : <CustomTable
                                        data={data}
                                        total={data.length}
                                        onScroll={onScroll}
                                        sort={sort}
                                        isReadOnly={patient => isAdmin && patient.userId !== profile.id}
                                    />}
                                </div> : searchValue === '' && <div className={styles.notFoundWrapper}>
                                    <div className={styles.notFoundIcon}>
                                        <SVGS.CloseIcon />
                                    </div>
                                    <h3>{isAdmin ? 'No patients found.' : 'You don\'t have any patients.'}</h3>
                                    <p>It is possible to create patients from mobile nurehab application, installed on your smartphone or tablet.</p>
                                </div>
                            }
                        </>
                    }
                </>
            }
        </Box>
    </PageWrapper>
};
